// Outreach / Messaging metrics — schema v1.
//
// Produced by the Layer 0 snapshot task (outreach_metrics_<accountId>_<date>.json)
// and bridged into Firestore at accounts/{accountId}/outreach/latest by the
// syncOutreachMetrics Cloud Function. Carries no secrets. The user-facing screen
// labels this "Messaging"; the schema keeps the producer's "outreach" vocabulary.

export interface OutreachMetrics {
  schemaVersion: 1;
  account: { id: string; name: string };
  generatedAt: string;
  channels: Channel[];
  history: HistoryPoint[];
  // Added by the Cloud Function bridge when writing to Firestore.
  _sync?: {
    fileId: string;
    sourceFile: string;
    driveCreatedTime: string;
    syncedAt: string;
  };
}

// Normalized cross-channel funnel stages, in order. Every channel maps its own
// raw stages onto these so channels can be summed and compared.
export interface NormalizedFunnel {
  top: number;
  reached: number;
  replied: number;
  interested: number;
  meeting: number;
  won: number;
}

export interface Channel {
  id: string; // "ig" | "li" | "em" | future
  type: string; // "instagram_dm" | "linkedin" | "email" | ...
  label: string;
  normalized: NormalizedFunnel;
  rawFunnel: Record<string, number>; // channel-specific stages, in order
  segments?: { byNiche?: Seg[]; byBand?: BandSeg[] };
  extra?: Record<string, any>; // campaigns[], email health, etc.
}

export interface Seg {
  niche: string;
  leads: number;
  sent: number;
  replies: number;
}

export interface BandSeg {
  band: string;
  leads: number;
  sent: number;
  replies: number;
}

export interface HistoryPoint {
  date: string;
  perChannel: Record<string, { top: number; reached: number; replied: number }>;
}

// The ordered normalized funnel stages, with display labels, used by the UI.
export const NORMALIZED_STAGES: { key: keyof NormalizedFunnel; label: string }[] =
  [
    { key: "top", label: "Top of funnel" },
    { key: "reached", label: "Reached" },
    { key: "replied", label: "Replied" },
    { key: "interested", label: "Interested" },
    { key: "meeting", label: "Meeting" },
    { key: "won", label: "Won" },
  ];

// ─── Leads (the VA's CRM, managed via Claude + MCP; read by the dashboard) ─────

// Stages are no longer a fixed enum — each account (community) defines its own
// pipeline in a config doc at Outreach/{accountId}/state/stageConfig, editable
// from the dashboard and via the MCP (get/set_outreach_stages). A lead's `stage`
// is therefore just a string key that should match one of the configured stages;
// unknown keys still render (in a trailing "archived" column) so renaming or
// deleting a stage never makes a lead disappear. This shape is duplicated in the
// MCP (mcp/src/repos/outreach.ts) and the Cloud Function (functions/src/outreach.ts)
// — keep the three in sync by hand (there is no shared package).
export type LeadStage = string;

// The preset tones a stage can wear. Kept small so the picker stays simple; each
// maps to an existing theme variable for the chip variant and the column dot.
export const STAGE_TONES = [
  "neutral",
  "info",
  "accent",
  "success",
  "warning",
  "danger",
] as const;
export type StageTone = (typeof STAGE_TONES)[number];

export interface StageDef {
  key: string; // slug: [a-z0-9_]+
  label: string;
  tone?: StageTone;
  // Terminal stages (won/lost-style) sit at the end of the pipeline: excluded
  // from the "active" board by default and from the funnel roll-up.
  terminal?: boolean;
  // For terminal stages only: whether reaching it is a positive ("won") or
  // negative ("lost") outcome. Drives the Pipeline tab cards and dedupe scoring.
  outcome?: "positive" | "negative";
}

export interface StageConfig {
  stages: StageDef[];
  updatedAt?: string;
}

// The built-in pipeline. Used as the fallback whenever an account has no saved
// stageConfig yet, so existing leads and all prior behavior keep working
// untouched (additive — nothing throws on the old stage keys).
export const DEFAULT_STAGES: StageDef[] = [
  { key: "top", label: "Top of funnel", tone: "neutral" },
  { key: "reached", label: "Reached", tone: "info" },
  { key: "replied", label: "Replied", tone: "info" },
  { key: "interested", label: "Interested", tone: "accent" },
  { key: "meeting", label: "Meeting", tone: "accent" },
  { key: "won", label: "Won", tone: "success", terminal: true, outcome: "positive" },
  { key: "lost", label: "Lost", tone: "danger", terminal: true, outcome: "negative" },
];

export const DEFAULT_STAGE_CONFIG: StageConfig = { stages: DEFAULT_STAGES };

// Ordered default keys — a few call sites still want the plain list.
export const LEAD_STAGES: string[] = DEFAULT_STAGES.map((s) => s.key);

export interface OutreachMessage {
  from: "me" | "lead";
  text: string;
  at?: string;
  channel?: string;
}

// ─── Draft review queue ───────────────────────────────────────────────────────
// The daily outreach run writes every personalized message draft to
// Outreach/{accountId}/queue/{draftId} BEFORE anything sends, so the copy is
// always on the record. A human approves, edits, or rejects them here in the
// dashboard; LinkedIn connection notes may also be approved by the run itself
// under the operator's direct-send authorization (autoApproved below). Only
// approved drafts ship. Shape mirrors mcp/src/repos/outreachQueue.ts and
// functions/src/outreach.ts — keep the three in sync by hand.

export type DraftKind = "li_dm" | "ig_dm" | "x_dm" | "email";

export type DraftStatus =
  | "pending"
  | "held"
  | "approved"
  | "shipping"
  | "rejected"
  | "shipped"
  | "error";

export interface DraftMessage {
  // Message role in the sequence: "connection_note" | "followup_1" |
  // "followup_2" | "dm" | "email_1" | ...
  slot: string;
  // Optional step ordinal for senders that address steps by number.
  stepNumber?: number;
  content: string;
}

export interface OutreachDraft {
  id: string;
  kind: DraftKind;
  status: DraftStatus;
  leadName: string;
  leadId?: string;
  handle?: string;
  externalId?: string;
  // The ONE real profile detail the messages are built on (rule zero). A
  // "held" draft has none and needs a human-supplied anchor before approval.
  anchor?: string;
  language?: string;
  messages: DraftMessage[];
  shipTarget?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNote?: string;
  // True when the unattended run approved this draft itself, under the
  // operator's direct-send authorization, instead of a human approving it here.
  // Only LinkedIn connection notes can reach approved that way, and the server
  // caps how many per UTC day. Use it in the Review tab to show which sends
  // nobody looked at, and autoApprovedAt for when.
  autoApproved?: boolean;
  autoApprovedAt?: string;
  // The run's daily direct-send cap is a live query over autoApprovedAt, not a
  // stored counter, so a draft that was auto-approved but provably never sent
  // hands its slot back: autoApprovedAt is cleared and these record that it
  // happened. autoApproved stays true — the run did approve it. A draft with
  // autoApprovalRefundedAt and no autoApprovedAt was approved by the run and
  // sent nothing.
  autoApprovalRefundedAt?: string;
  autoApprovalRefundedFrom?: string;
  // The last ship attempt proved nothing was sent (the shipper said so
  // explicitly). Absent or false means the outcome could not rule a send out.
  lastShipProvenNoSend?: boolean;
  shippedAt?: string;
  shipResult?: Record<string, any>;
  error?: string;
  leaseOwner?: string;
  leaseUntil?: string;
  shipAttempts?: number;
  shipStartedAt?: string;
  lastShipErrorKind?: string;
  nextShipAttemptAt?: string;
  retryable?: boolean;
}

// Summary the daily run writes to Outreach/{accountId}/state/daily_run —
// surfaced in the Review tab so you can see when the pipeline last ran.
export interface OutreachRunSummary {
  at?: string;
  perChannel?: Record<
    string,
    {
      discovered?: number;
      kept?: number;
      removed?: number;
      drafted?: number;
      held?: number;
      shipped?: number;
      shipErrors?: number;
    }
  >;
  [key: string]: any;
}

// ─── Founder Instagram session health ────────────────────────────────────────

export type InstagramHealthVerdict =
  | "green"
  | "yellow"
  | "red"
  | "unknown";

export interface InstagramSessionHealthUnavailable {
  available: false;
  accountId: string;
  reason: "not_founder_canary" | string;
  note: string;
}

export interface InstagramSessionHealthAvailable {
  available: true;
  accountId: string;
  generatedAt: string;
  overall: InstagramHealthVerdict;
  operational: {
    verdict: InstagramHealthVerdict;
    reasons: string[];
    sessionStatus: string;
    attempts: number;
    sent: number;
    ambiguous: number;
    definitiveFailures: number;
  };
  inboxPlacement: {
    verdict: InstagramHealthVerdict;
    confidence: "low" | "medium";
    reasons: string[];
    recentSeedChecks: number;
    concerningSeedChecks: number;
    note: string;
  };
  outcomes: {
    verdict: InstagramHealthVerdict;
    reasons: string[];
    sent: number;
    seen: number;
    replies: number;
    seenRate: number | null;
    replyRate: number | null;
    note: string;
  };
  runtime: {
    workerEnabled: boolean;
    browserAdapterReady: boolean;
    armed: boolean;
    dailyCap: number;
    minSendGapSeconds: number;
    engagementEnabled?: boolean;
    engagementAdapterReady?: boolean;
    followDailyCap?: number;
    commentDailyCap?: number;
    minActionGapSeconds?: number;
    lastWorkerBootAt: string | null;
  };
  session: {
    status: string;
    lastOkAt: string | null;
    lastActionAt: string | null;
    sendLeaseUntil: string | null;
    challengeReason: string | null;
    disabledReason: string | null;
  };
  quota: {
    date: string;
    firstContactAttempts: number;
    firstContactSent: number;
    ambiguousAttempts: number;
    failedAttempts: number;
    followAttempts?: number;
    followCompleted?: number;
    followAmbiguous?: number;
    followFailures?: number;
    commentAttempts?: number;
    commentCompleted?: number;
    commentAmbiguous?: number;
    commentFailures?: number;
  };
}

export type InstagramSessionHealth =
  | InstagramSessionHealthUnavailable
  | InstagramSessionHealthAvailable;

export type InstagramSequenceStatus =
  | "scheduled"
  | "running"
  | "paused"
  | "completed"
  | "failed"
  | "needs_review"
  | "cancelled";

export type InstagramSequenceActionStatus =
  | "scheduled"
  | "running"
  | "completed"
  | "already_completed"
  | "failed"
  | "ambiguous"
  | "cancelled";

export interface InstagramSequenceAction {
  id: string;
  type: "follow" | "comment" | "dm";
  dueAt: string;
  status: InstagramSequenceActionStatus;
  startedAt?: string;
  completedAt?: string;
  outcome?: string;
  error?: string;
}

export interface InstagramLeadSequence {
  id: string;
  accountId: string;
  leadId: string;
  username: string;
  status: InstagramSequenceStatus;
  actions: InstagramSequenceAction[];
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  error?: string;
}

export interface OutreachLead {
  id: string;
  name: string;
  channel: string; // "ig" | "li" | "em" | ...
  stage: LeadStage;
  handle?: string;
  email?: string;
  company?: string;
  niche?: string;
  nextAction?: string;
  nextActionAt?: string; // YYYY-MM-DD
  lastContactedAt?: string;
  notes?: string;
  owner?: string;
  source?: string;
  externalId?: string;
  data?: Record<string, any>;
  // Instagram sends (recordInstagramSend) also stamp origin +
  // firstContact on the touch: read those to tell a machine send from an
  // operator send, never the prose. Every other touch carries at + note.
  touches?: {
    at: string;
    note: string;
    origin?: string;
    firstContact?: boolean;
  }[];
  conversation?: OutreachMessage[];
  customerUid?: string;
  createdAt?: string;
  updatedAt?: string;
  // Returned by the list endpoint (which omits the heavy arrays themselves).
  messageCount?: number;
  touchCount?: number;
}

// ─── Send-rail capacity snapshots ────────────────────────────────────────────
//
// One doc per UTC day at Outreach/{accountId}/rails/{YYYY-MM-DD}, written by
// the daily social routines through the MCP and read here through the
// listOutreachRailSnapshots callable. A rail is one send lane: a channel, a
// worker account, and a unit. `allowed` is what the lane could do that day,
// `delivered` is what it actually did.
//
// The MCP side (mcp/src/repos/outreachRails.ts) holds the authoritative enums
// for status, channel and unit. They are plain `string` here on purpose, per
// the cross-project type convention: a new status value added MCP-side must
// never break the admin build.
export interface OutreachRail {
  id: string; // "{channel}:{account}:{unit}", derived server-side
  channel: string; // "li" | "ig" | "em" | "x" | "inmail"
  account: string; // worker/session account, NOT the outreach account id
  label: string;
  unit: string; // "invites" | "messages" | "dms" | "emails" | ...
  allowed: number;
  delivered: number;
  staged?: number;
  queueBehind?: number;
  freshCopy?: number;
  // "ok" | "under" | "silent" | "disarmed" | "dead" | "blocked" | "manual"
  status: string;
  statusReason?: string;
  lastDeliveredAt?: string | null;
  source?: string;
  writtenAt?: string;
}

export interface OutreachHumanQueue {
  count: number;
  oldestDays?: number;
  items?: string[];
}

export interface OutreachRailSnapshot {
  date: string; // UTC day, YYYY-MM-DD
  updatedAt: string | null;
  rails: Record<string, OutreachRail>;
  humanQueue: OutreachHumanQueue | null;
  totals: { allowed: number; delivered: number };
}

// ─── Daily outreach analysis ─────────────────────────────────────────────────
//
// One doc per UTC day at Outreach/{accountId}/analysis/{YYYY-MM-DD}, written by
// the daily analysis routine through the MCP and read here through the
// listOutreachAnalyses callable. This is the judgement half of the dashboard:
// the numbers live in the rail snapshots and in the lead counts the screen
// already computes.
//
// The MCP side (mcp/src/repos/outreachAnalysis.ts) holds the authoritative
// enums for tone, priority, group and status. They are plain `string` here on
// purpose, per the cross-project type convention: a new value added MCP-side
// must never break the admin build.
export interface OutreachAnalysisTile {
  label: string;
  value: string;
  detail?: string;
  tone?: string; // "crit" | "warn" | "good" | "neutral"
}

export interface OutreachAnalysisFinding {
  id: string; // stable slug, reused across days while the issue stays open
  priority: string; // "P0" | "P1" | "P2"
  group: string; // "send" | "replies" | "human" | "hygiene"
  title: string;
  body: string;
  fix: string;
  railId?: string;
  status: string; // "open" | "done" | "dismissed"
}

export interface OutreachAnalysis {
  date: string; // UTC day, YYYY-MM-DD
  generatedAt: string | null;
  source: string | null;
  updatedAt: string | null;
  verdict: {
    tone: string; // "crit" | "warn" | "good"
    headline: string;
    detail?: string;
  } | null;
  tiles: OutreachAnalysisTile[];
  // What each rail's health check hides, keyed by rail id.
  railNotes: Record<string, string>;
  findings: OutreachAnalysisFinding[];
  sources: string[];
}
